/**
 * useAuth — reactive wrapper around the auth adapter.
 *
 *   const { isAuthed, user, logout } = useAuth();
 *
 * Components that read auth state should use this rather than calling
 * authStore directly, so React re-renders on login/logout/profile-update.
 */

import { useEffect, useState } from "react";
import authStore from "@/lib/auth";
import type { AuthRecord } from "@/lib/auth";
import { syncAuthCookie } from "@/lib/files";

// Seed the session cookie at module load so a page reload has the
// cookie before any <img> fires. Subsequent login / logout transitions
// are handled by the subscription in the hook body. `syncAuthCookie`
// is async now — we fire and forget at module load; callers that
// actually depend on the cookie being established (login flow) should
// `await` it explicitly before navigating.
void syncAuthCookie();

export function useAuth() {
  const [user, setUser] = useState<AuthRecord | null>(authStore.record);
  const [isAuthed, setIsAuthed] = useState(authStore.isValid);

  useEffect(() => authStore.onChange((_token, record) => {
    setUser(record);
    setIsAuthed(authStore.isValid);
    void syncAuthCookie();
  }), []);

  return {
    isAuthed,
    user,
    logout: () => authStore.clear(),
    refresh: () => authStore.refresh?.(),
  };
}
